import React from "react"
import {
	Assignee,
	AssignmentActionRequestType,
	AssignmentStage,
	AssignmentStatus,
	GeneralReviewApproveRequest,
	GeneralReviewAssignmentItemPayload,
	GeneralReviewEscalateRequest,
	GeneralReviewRejectReason,
	GeneralReviewRejectRequest,
	update,
} from "../../resources/assignments"
import {Resource} from "../../resources/common"
import {useResetScrollTop} from "../../hooks/useResetScrollTop"
import {
	AssignmentPageItem,
	AssignmentRiskFlag,
	AssignmentRiskFlagSeverity,
} from "../AssignmentPageItem/AssignmentPageItem"
import {AssignmentPageItemApproveRejectFooter} from "../AssignmentPageItemApproveRejectFooter/AssignmentPageItemApproveRejectFooter"
import {Claims} from "../../services/auth"
import {useModal} from "react-modal-hook"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {AssignmentApproveRejectModal} from "../AssignmentApproveRejectModal/AssignmentApproveRejectModal"
import {AssignmentStatusUpdateReasonsMapping} from "../../resources/assignments"
import {AssignmentPageCompletedItemHeader} from "../AssignmentPageCompletedItemHeader/AssignmentPageCompletedItemHeader"
import {CheckDeposit} from "../../resources/checkDeposits"
import {AssignmentPageItemGeneralReviewCheckDepositDetails} from "./AssignmentPageItemGeneralReviewCheckDepositDetails"
import {AssignmentEscalateModal} from "../AssignmentEscalateModal/AssignmentEscalateModal"
import {CheckImageCard} from "../CheckDeposits/CheckImageCard"
import {waitingForOcrLabel} from "../CheckDeposits/checkDepositActions"
import {Panel, PanelGroup, PanelResizeHandle} from "react-resizable-panels"
import {Badge} from "../Badge/Badge"
import {useToasts} from "react-toast-notifications"
import Admonition from "../Admonitions/Admonition"

interface AssignmentPageItemGeneralReviewCheckDepositProps {
	id: string
	assignees: Assignee[]
	status: AssignmentStatus
	stage: AssignmentStage
	title: string
	description?: string | JSX.Element
	createdAt: Date
	statusUpdatedAt?: Date
	isOwnersItem?: boolean
	checkDeposit?: CheckDeposit
	payload: GeneralReviewAssignmentItemPayload
	accessToken: string
	refresh: () => void
	onFinalActionSuccess?: () => void
	refreshToken: number
	disabled?: boolean
	claims?: Claims
	frontImage?: ArrayBuffer | null | undefined
	backImage?: ArrayBuffer | null | undefined
	frontTiffImage?: ArrayBuffer | null
	backTiffImage?: ArrayBuffer | null
	expectsTiff?: boolean
	included?: Resource[]
}

export enum GeneralReviewCheckDepositStatusUpdateReasonKeys {
	BlankCheck = "Blank check",
	MissingDate = "Missing date",
	PostDated = "Post dated",
	StaleDated = "Stale dated",
	MissingSignature = "Missing signature",
	MissingEndorsement = "Missing endorsement",
	AmountMismatchMDC = "Amount mismatch - MDC doesn't match check",
	AmountMismatchLAR = "Amount mismatch - Legal text doesn't match numeric amount",
	DuplicatedCheck = "Duplicated check",
	PartialEndorsement = "Partial endorsement",
	NameMismatchBeneficiary = "Name mismatch - Beneficiary",
	IneligibleItemScreenCapturedImage = "Ineligible item - Screen Captured Image",
	IneligibleItemFoldedOrTornCorners = "Ineligible item - Folded or Torn Corners",
	AlteredFictitiousItem = "Altered/Fictitious item",
	IneligibleItem = "Ineligible Item",
	UnusableImage = "Unusable Image",
	Other = "Other",
}

const generalReviewCheckDepositAssignmentStatusUpdateReasons: AssignmentStatusUpdateReasonsMapping = {
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.BlankCheck]: {
		reason: "BlankCheck",
		displayName: "Blank Check",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.MissingDate]: {
		reason: "MissingDate",
		displayName: "Missing Date",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.PostDated]: {
		reason: "PostDated",
		displayName: "Post Dated",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.StaleDated]: {
		reason: "StaleDated",
		displayName: "Stale Dated",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.MissingSignature]: {
		reason: "MissingSignature",
		displayName: "Missing Signature",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.MissingEndorsement]: {
		reason: "MissingEndorsement",
		displayName: "Missing Endorsement",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.AmountMismatchMDC]: {
		reason: "AmountMismatchMDC",
		displayName: "Amount Mismatch - MDC doesn't match check",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.AmountMismatchLAR]: {
		reason: "AmountMismatchLAR",
		displayName: "Amount Mismatch - Legal text doesn't match numeric amount",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.DuplicatedCheck]: {
		reason: "DuplicatedCheck",
		displayName: "Duplicated Check",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.PartialEndorsement]: {
		reason: "PartialEndorsement",
		displayName: "Partial Endorsement",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.NameMismatchBeneficiary]: {
		reason: "NameMismatchBeneficiary",
		displayName: "Name Mismatch - Beneficiary",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.IneligibleItemScreenCapturedImage]: {
		reason: "IneligibleItemScreenCaptured",
		displayName: "Ineligible Item - Screen Captured Image",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.IneligibleItemFoldedOrTornCorners]: {
		reason: "IneligibleItemFoldedTornCorners",
		displayName: "Ineligible Item - Folded or Torn Corners",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.AlteredFictitiousItem]: {
		reason: "AlteredFictitiousItem",
		displayName: "Altered/Fictitious Item",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.IneligibleItem]: {
		reason: "IneligibleItem",
		displayName: "Ineligible Item",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.UnusableImage]: {
		reason: "UnusableImage",
		displayName: "Unusable Image",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.Other]: {reason: "Other", displayName: "Other"},
}

export function AssignmentPageItemGeneralReviewCheckDeposit({
	id,
	assignees,
	status,
	stage,
	title,
	description,
	createdAt,
	statusUpdatedAt,
	isOwnersItem,
	checkDeposit,
	payload: {reason, reasonText, isEscalated},
	accessToken,
	refresh,
	onFinalActionSuccess,
	refreshToken,
	disabled,
	claims,
	frontImage,
	backImage,
	frontTiffImage,
	backTiffImage,
	expectsTiff,
	included,
}: AssignmentPageItemGeneralReviewCheckDepositProps) {
	const checkDepositDetailsContentRef = React.useRef<HTMLDivElement>(null)
	useResetScrollTop(checkDepositDetailsContentRef, [id])
	const {addToast} = useToasts()

	const validateCheckDepositFields = () => {
		if (!checkDeposit) {
			addToast("Check deposit data is not available. Cannot approve.", {
				appearance: "error",
			})
			return false
		}

		const missingFields: string[] = []

		if (!checkDeposit.attributes.checkNumber) {
			missingFields.push("Check Number")
		}
		if (!checkDeposit.attributes.counterparty.accountNumber) {
			missingFields.push("Counterparty Account Number")
		}
		if (!checkDeposit.attributes.counterparty.routingNumber) {
			missingFields.push("Counterparty Routing Number")
		}
		if (!checkDeposit.attributes.micr) {
			missingFields.push("MICR")
		}
		if (!checkDeposit.attributes.amount) {
			missingFields.push("Amount")
		}

		if (missingFields.length > 0) {
			const fieldsList = missingFields.join(", ")
			addToast(`Missing required field(s): ${fieldsList}. Please edit before approving.`, {
				appearance: "error",
			})
			return false
		}

		return true
	}

	const [showEscalateModal, hideEscalateModal] = useModal(() => {
		const successText = "Escalated successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = ({reasonText}: {reasonText: string}) => {
			const data: GeneralReviewEscalateRequest = {
				type: AssignmentActionRequestType.generalReviewEscalateRequest,
				attributes: {
					reasonText,
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentEscalateModal
				title="Are you sure you want to escalate?"
				state={state}
				onSubmit={onSubmit}
				withReasonText
				primaryButtonText="Escalate Item"
				primaryButtonClassname="is-outlined"
				closeModal={hideEscalateModal}
				onSuccess={onFinalActionSuccess ?? refresh}
				successText={successText}
				successClassname="has-text-black"
			/>
		)
	}, [id, onFinalActionSuccess, refresh])

	const [showApprove, hideApprove] = useModal(() => {
		const successText = "Approved successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = (args: {reasonText: string} | null) => {
			const data: GeneralReviewApproveRequest = {
				type: AssignmentActionRequestType.generalReviewApproveRequest,
				attributes: {
					reasonText: args?.reasonText,
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Approve Item"
				description={
					<>
						<p>
							Are you sure you want to approve <span className="has-text-weight-bold">{`General Review #${id}?`}</span>
						</p>
						<Admonition
							type="is-warning"
							message="Have you also reviewed the TIFF version of both sides of the check deposit to ensure they're clear and legible? Otherwise, the check might be returned."
						/>
					</>
				}
				state={state}
				onSubmit={onSubmit}
				withReasonText
				isOptionalReasonText
				reasonTextPlaceholder="Please provide a comment if there were initial concerns cleared by additional outreach or investigation."
				primaryButtonText="Approve"
				primaryButtonClassname="is-success"
				closeModal={hideApprove}
				onSuccess={onFinalActionSuccess ?? refresh}
				successText={successText}
			/>
		)
	}, [id, onFinalActionSuccess, refresh])

	const [showReject, hideReject] = useModal(() => {
		const successText = "Rejected successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = ({reason, reasonText}: {reason: GeneralReviewRejectReason; reasonText?: string}) => {
			const data: GeneralReviewRejectRequest = {
				type: AssignmentActionRequestType.generalReviewRejectRequest,
				attributes: {
					reason,
					...(reason === GeneralReviewRejectReason.Other ? {reasonText} : {}),
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Reject Item"
				state={state}
				onSubmit={onSubmit}
				withReasonText
				reasonTextPlaceholder="Why are you rejecting this based on general review?"
				reasons={generalReviewCheckDepositAssignmentStatusUpdateReasons}
				reasonsToEnableReasonText={[GeneralReviewCheckDepositStatusUpdateReasonKeys.Other]}
				primaryButtonText="Reject"
				primaryButtonClassname="is-danger"
				closeModal={hideReject}
				onSuccess={onFinalActionSuccess ?? refresh}
				successText={successText}
				successClassname="has-text-black"
			/>
		)
	}, [id, onFinalActionSuccess, refresh])

	const assignmentFlags: AssignmentRiskFlag[] =
		checkDeposit?.attributes.riskFlags?.map((rf) => ({
			name: rf.name,
			severity: rf.severity as AssignmentRiskFlagSeverity,
			description: [rf.description, rf.resolution].filter(Boolean).join(" ") || undefined,
		})) ?? []

	const waitingForOcr = checkDeposit?.attributes.waitingForOcr === true

	const handleApprove = () => {
		if (validateCheckDepositFields()) {
			showApprove()
		}
	}

	const escalatedBadge = isEscalated && (
		<Badge
			text="Escalated"
			className="assignment-page-item-escalated-badge"
			tooltip={status !== AssignmentStatus.Approved ? reasonText : undefined}
			tooltipPlace="right"
			tooltipKey="assignment-escalated-tooltip"
			icon="information-circle--interface-essential"
		/>
	)

	return (
		<AssignmentPageItem
			assignees={assignees}
			status={status}
			stage={stage}
			title={title}
			description={description}
			createdAt={createdAt}
			statusUpdatedAt={statusUpdatedAt}
			isOwnersItem={isOwnersItem}
			disabled={disabled}
			assignmentFlags={assignmentFlags}
			escalatedBadge={escalatedBadge}
			pageItemFooter={
				!disabled && !waitingForOcr ? (
					<AssignmentPageItemApproveRejectFooter
						approveLabel="Approve"
						rejectLabel="Reject"
						onApprove={handleApprove}
						onReject={showReject}
						onEscalate={showEscalateModal}
						escalateLabel={isEscalated ? undefined : "Escalate"}
					/>
				) : undefined
			}
			pageCompletedItemHeader={
				<AssignmentPageCompletedItemHeader
					assignees={assignees}
					status={status}
					reason={reason}
					reasonText={reasonText}
					escalatedBadge={escalatedBadge}
				/>
			}
		>
			{waitingForOcr && (
				<div className="check-deposit-waiting-for-ocr notification is-warning" role="status">
					{waitingForOcrLabel}
				</div>
			)}
			<PanelGroup className="panel-group" direction="horizontal" autoSaveId="persistence">
				<Panel defaultSize={30} minSize={25} className="panel-group-left">
					<AssignmentPageItemGeneralReviewCheckDepositDetails
						id={id}
						checkDeposit={checkDeposit}
						disabled={disabled}
						claims={claims}
						refresh={refresh}
						refreshToken={refreshToken}
						included={included}
					/>
				</Panel>
				<PanelResizeHandle className="panel-group-resize-handle">
					<div className="panel-group-resize-handle-left" />
					<div className="panel-group-resize-handle-right" />
				</PanelResizeHandle>
				<Panel minSize={40}>
					<div className="panel-group-right">
						{checkDeposit && (
							<CheckImageCard
								frontImage={frontImage}
								backImage={backImage}
								frontTiffImage={frontTiffImage}
								backTiffImage={backTiffImage}
								expectsTiff={expectsTiff}
								id={checkDeposit.id}
								status={checkDeposit.attributes.status}
								compact={true}
							/>
						)}
					</div>
				</Panel>
			</PanelGroup>
		</AssignmentPageItem>
	)
}
